class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  // カートページにはドロワーも同居するため、自分がどちらかを判別して
  // 必ず自分側の要素だけを参照する（document 全体を曖昧に探さない）
  get isDrawer() {
    return this.tagName === 'CART-DRAWER-ITEMS';
  }

  constructor() {
    super();
    this.lineItemStatusElement = this.isDrawer
      ? document.getElementById('CartDrawer-LineItemStatus')
      : document.getElementById('shopping-cart-line-item-status');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  // publish 時の source 識別子（自分発の更新では再取得しない。
  // カートページではドロワーとページのリストが同居するため、相手発の更新では再取得して同期する）
  get updateSource() {
    return this.isDrawer ? 'cart-drawer-items' : 'cart-items';
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === this.updateSource) {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    // 数量ルール（B2B）は未使用のため min/max/step の検証は行わない
    if (Number.isNaN(inputValue) || inputValue < 0) {
      event.target.value = event.target.getAttribute('value');
      return;
    }

    event.target.setCustomValidity('');
    event.target.reportValidity();
    this.updateQuantity(
      index,
      inputValue,
      event,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.isDrawer) {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // 明細リストとフッター（合計）をまとめて最新化する
      // ※ ブロック設定を保つため、ファイル名ではなくテンプレートのセクションID（data-id）で取得する
      const itemsSectionId = document.getElementById('main-cart-items')?.dataset.id;
      const footerSectionId = document.getElementById('main-cart-footer')?.dataset.id;
      const sectionIds = [itemsSectionId, footerSectionId].filter(Boolean);
      if (sectionIds.length === 0) return Promise.resolve();

      return fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`)
        .then((response) => response.json())
        .then((sections) => {
          if (sections[itemsSectionId]) {
            const itemsHtml = new DOMParser().parseFromString(sections[itemsSectionId], 'text/html');
            const sourceQty = itemsHtml.querySelector('cart-items');
            if (sourceQty) this.innerHTML = sourceQty.innerHTML;
          }

          const targetFooter = document.getElementById('main-cart-footer');
          if (targetFooter && sections[footerSectionId]) {
            const footerHtml = new DOMParser().parseFromString(sections[footerSectionId], 'text/html');
            const sourceFooter = footerHtml.querySelector('#main-cart-footer');
            if (sourceFooter) targetFooter.innerHTML = sourceFooter.innerHTML;
          }

          const isEmpty = !this.querySelector('.cart-item');
          this.classList.toggle('is-empty', isEmpty);
          if (targetFooter) targetFooter.classList.toggle('is-empty', isEmpty);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement = this.isDrawer
            ? document.getElementById(`Drawer-quantity-${line}`)
            : document.getElementById(`Quantity-${line}`);
          const items = this.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            // 在庫上限などの 422 時、サーバー側では上限まで数量が変わっている場合があるため
            // 表示を最新化してからエラーメッセージを出す
            quantityElement.value = quantityElement.getAttribute('value');
            Promise.resolve(this.onCartUpdate()).then(() => {
              this.updateLiveRegions(line, parsedState.errors);
              // カートページではドロワーとページ側リストが同居しているため、相手側も同期させる
              publish(PUB_SUB_EVENTS.cartUpdate, { source: this.updateSource, variantId: variantId });
            });
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            // ヘッダーのカートアイコン非表示時など、対象が無ければスキップ
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement || !parsedState.sections[section.section]) return;
            const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          // フォーカス移動は補助機能。失敗してもカート更新処理（この後の publish）を止めない
          try {
            const lineItem = this.isDrawer
              ? document.getElementById(`CartDrawer-Item-${line}`)
              : document.getElementById(`CartItem-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              // 空カート表示には閉じるボタンしかフォーカス対象がない
              const emptyContainer = cartDrawerWrapper.querySelector('.drawer__inner-empty');
              const focusTarget = emptyContainer?.querySelector('.drawer__close');
              if (emptyContainer && focusTarget) trapFocus(emptyContainer, focusTarget);
            } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
            }
          } catch (focusError) {
            console.error(focusError);
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: this.updateSource, cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = this.isDrawer
          ? document.getElementById('CartDrawer-CartErrors')
          : document.getElementById('cart-errors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = this.isDrawer
      ? document.getElementById(`CartDrawer-LineItemError-${line}`)
      : document.getElementById(`Line-item-error-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    // 再描画後でも生きている要素を毎回取り直す
    const lineItemStatus = this.isDrawer
      ? document.getElementById('CartDrawer-LineItemStatus')
      : document.getElementById('shopping-cart-line-item-status');
    lineItemStatus?.setAttribute('aria-hidden', true);

    const cartStatus = this.isDrawer
      ? document.getElementById('CartDrawer-LiveRegionText')
      : document.getElementById('cart-live-region-text');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = this.isDrawer
      ? document.getElementById('CartDrawer-CartItems')
      : document.getElementById('main-cart-items');
    mainCartItems?.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = this.isDrawer
      ? document.getElementById('CartDrawer-CartItems')
      : document.getElementById('main-cart-items');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
